import { Stack, Typography, Tooltip } from "@mui/material";
import { DescriptionOutlined, Person } from "@mui/icons-material";
import dayjs from "dayjs";
import { toLocaleDateTimeString } from "@utils";
import { OrderOut } from "@providers/hop-ord-server/api";
import { PAGE_HEADER_HEIGHT } from "@constants";

const calculateAge = (dateOfBirth: string): number | null => {
  if (!dayjs(dateOfBirth).isValid()) {
    return null;
  }

  return dayjs().diff(dateOfBirth, "year");
};

const formatPatientNameAndAge = (name: string, age: number | null): string => {
  const hasValidName = name.trim().length > 0;
  const hasValidAge = age !== null && !isNaN(age);

  if (hasValidName && hasValidAge) {
    return `${name}, ${age}`;
  }

  if (hasValidName) {
    return name;
  }

  if (hasValidAge) {
    return `${age}`;
  }

  return "-";
};

interface PageHeaderProps {
  orderData: OrderOut | undefined;
}

const PageHeader = ({ orderData }: PageHeaderProps) => {
  const name = orderData?.patientName ?? "";
  const dateOfBirth = orderData?.patientDob ?? "";
  const patientId = orderData?.patientMrn ?? "";
  const careplanName = orderData?.orderName ?? "";

  const age = calculateAge(dateOfBirth);

  const nameAndAgeText = formatPatientNameAndAge(name, age);
  const careplanNameText = careplanName === "New Order" ? "-" : careplanName;

  return (
    <Stack
      direction="row"
      sx={{
        gap: 4,
        paddingX: 2,
        paddingY: 1,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        zIndex: 100,
        maxHeight: PAGE_HEADER_HEIGHT,
        minHeight: PAGE_HEADER_HEIGHT,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 2,
          flexShrink: 0,
          alignItems: "center",
          overflow: "hidden",
          width: { xs: "calc(50%)", md: "unset" },
        }}
      >
        <Person fontSize="large" />
        <Stack sx={{ overflow: "hidden" }}>
          <Typography variant="subtitle1" noWrap>
            {nameAndAgeText}
          </Typography>
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            <Typography variant="body2" noWrap>
              Patient ID: {patientId || "-"}
            </Typography>
            <Typography variant="body2" noWrap>
              Date of Birth:{" "}
              {dateOfBirth ? toLocaleDateTimeString(dateOfBirth, "date") : "-"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        sx={{
          gap: 1,
          flexShrink: 1,
          width: "calc(50%)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: "center",
          }}
        >
          <DescriptionOutlined />
          <Typography variant="subtitle2" noWrap>
            Careplan
          </Typography>
        </Stack>
        <Tooltip title={careplanNameText} arrow>
          <Typography variant="body2" noWrap sx={{ maxWidth: "fit-content" }}>
            {careplanNameText}
          </Typography>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default PageHeader;
